import { keepPreviousData, useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { DashboardConfigPayload } from '@/admin/pages/dashboard/dashboard-types'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'

/**
 * The current tenant's dashboard config (roles / tiles / favourites / brand),
 * served by GET /api/dashboards/config/. The generic dashboard engine renders
 * whatever this returns - onboarding a new client is a new config row on the
 * backend, never an engine change. Config-time data, so cache generously.
 */
export function useDashboardConfig() {
  return useQuery<DashboardConfigPayload>({
    // 'v3' busts any persisted cache of the pre-doc-columns config; config is
    // config-time data but we refetch on mount so a re-seed shows immediately.
    queryKey: ['dashboard-config', 'v3'],
    queryFn: () => api.get('/dashboards/config/').then((r) => r.data),
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  })
}

/**
 * Computed, tenant-scoped dashboard metrics (GET /api/dashboards/metrics/). A
 * KPI item binds to one of these by key via its `metric` field; the number is
 * real, not seed data. Most metrics honour the dashboard's selected date window
 * (`createdAfter`) so 7d / 30d / Quarter / YTD recompute the headline numbers.
 */
export function useDashboardMetrics(createdAfter?: string | null) {
  return useQuery<Record<string, number>>({
    queryKey: ['dashboard-metrics', createdAfter ?? 'all'],
    queryFn: () =>
      api
        .get('/dashboards/metrics/', {
          params: createdAfter ? { created_after: createdAfter } : {},
        })
        .then((r) => r.data),
    staleTime: 30 * 1000,
  })
}

/** The card builder's building blocks from GET /api/dashboards/catalog/. */
export interface DashboardCatalog {
  /**
   * Shortlisted entities a card can be built from. `measures` lists the measure
   * keys THIS entity can fulfil - the builder offers only these so no
   * combination lands on an empty data path.
   */
  entities: Array<{
    key: string
    label: string
    measures: string[]
    /** A measure's name for this entity where it differs ("Open over time"). */
    measure_labels?: Record<string, string>
    /** The measures (groupings, and "total") a card can show several measures for. */
    grouped?: string[]
    /** The measures such a card offers here ("count", "amount_paid", ...). */
    metrics?: string[]
  }>
  /** Master measure metadata (key -> label + resulting data shape). */
  measures: Array<{
    key: string
    label: string
    data_type: string
    /** Displays this measure offers beyond its data type's (e.g. "heat_map"). */
    extra_renders?: string[]
    /** When set, exactly the displays this measure offers (replaces the defaults). */
    renders?: string[]
  }>
  /**
   * The title each buildable card gets when the user does not name it, keyed
   * "<entity>:<measure>" - built server-side in the tenant's vocabulary with
   * the same rule as seeded tiles.
   */
  titles?: Record<string, string>
  /**
   * Raw data tables, keyed "<entity>:<measure>": the live-list binding (which
   * records, columns, sort) a card drawn as a Raw data table uses.
   */
  raw_tables?: Record<string, Record<string, unknown>>
  /** Every measure a card can show side by side. `share`: a share of the total. */
  metrics?: Array<{ key: string; label: string; share?: boolean }>
}

/** One column of a card that shows several measures. */
export interface MeasureColumn {
  /** "count", or "amount_paid:USD" for a money measure in one currency. */
  key: string
  label: string
  format: 'number' | 'percent' | 'money'
  currency?: string
}

/**
 * The catalogue for the Customise card builder: the shortlisted entities and the
 * measures they can combine with. Any entity x measure is buildable - the
 * combinations are open, not a fixed list. Tenant-agnostic and config-time.
 */
export function useDashboardCatalog() {
  return useQuery<DashboardCatalog>({
    // v4: flush any persisted cache from when the catalogue endpoint was
    // `reporting`-gated and returned [] for flag-off tenants (an empty array has
    // no `.entities`, so the card builder's data picker rendered empty).
    // v5: the catalogue now carries `titles` (WPS-232 #3); drop cached v4 copies.
    // v6: it carries the measures a card can show side by side.
    queryKey: ['dashboard-catalog', 'v6'],
    queryFn: () => api.get('/dashboards/catalog/').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    // Static reference data, but always revalidate on mount (stale-while-
    // revalidate) so a bad/empty cached value can never get stuck across loads.
    refetchOnMount: 'always',
  })
}

/** A shape-tagged dataset envelope from GET /api/dashboards/data/?key=... */
export interface DashboardDatasetEnvelope {
  data_type?: string
  /** snapshot_number: a single figure. */
  value?: number
  /** "money" -> render `value` in the org currency. */
  format?: string
  /** A money figure: its currency, and one figure per currency paid in. */
  currency?: string
  values?: Array<{ currency: string; value: number }>
  /** measure_table: the measures, one row per group, and their totals. */
  columns?: MeasureColumn[]
  rows?: Array<{ key: string; label: string; values: Record<string, number | null> }>
  totals?: Record<string, number | null>
  series?: Array<{ label: string; total: number; data: Array<{ period: string; value: number }> }>
  /** `key` is the stored option value when it differs from the label (field breakdowns). */
  segments?: Array<{ label: string; value: number; key?: string }>
  items?: Array<Record<string, unknown>>
  /** geo_regions (a card drawn as a heat map): records per state / province. */
  regions?: Array<{ code: string; label: string; value: number; product?: string }>
  /** geo_regions: records with no usable address (not on the map). */
  unplaced?: number
  /** snapshot_number: how many records the value covers (My cycle time). */
  count?: number
  /** The filters this card offers (e.g. product template, status, payment). */
  filters?: ProductFilterFacet[]
  /** Column names for the card's table ("Class" / "Total"). */
  labels?: { category?: string; value?: string; total?: string }
}

/**
 * One live dataset envelope (trend / breakdown / action-list), served by
 * GET /api/dashboards/data/?key=... A chart, leaderboard, or alerts tile that
 * carries a `data_set.key` binds to real, tenant-scoped data this way instead of
 * inline seed data. Honours the dashboard's date window. Disabled (no request)
 * when there is no key, so inline-data tiles never fetch.
 */
export function useDashboardDataset(
  key?: string | null,
  createdAfter?: string | null,
  /** "region": the same records per state / province (a heat map). */
  by?: 'region',
  /** The card's own filters (filter key -> chosen values). */
  filters?: Record<string, string[]>,
  /** Several measures side by side (a measure table). */
  measures?: string[],
) {
  const filterParams = Object.fromEntries(
    Object.entries(filters ?? {})
      .filter(([, v]) => v?.length)
      .map(([k, v]) => [`filter__${k}`, v.join(',')]),
  )
  const measureParam = measures?.length ? measures.join(',') : ''
  return useQuery<DashboardDatasetEnvelope>({
    queryKey: [
      'dashboard-dataset',
      key ?? '',
      createdAfter ?? 'all',
      by ?? '',
      filterParams,
      measureParam,
    ],
    queryFn: () =>
      api
        .get('/dashboards/data/', {
          params: {
            key,
            ...(createdAfter ? { created_after: createdAfter } : {}),
            ...(by ? { by } : {}),
            ...(measureParam ? { measures: measureParam } : {}),
            ...filterParams,
          },
        })
        .then((r) => r.data),
    enabled: !!key,
    staleTime: 30 * 1000,
    // Changing a card's filters keeps its current figures on screen until the
    // new ones arrive, so the card (and its open filter panel) never blanks.
    placeholderData: keepPreviousData,
  })
}
